<template>
  <div class="magazine-issue-grid__container">
    <h1 style="display: none;">LATEST ISSUE:</h1>

    <div class="magazine-issue-grid__hero">
      <TheMagazineCard
        v-if="featuredArticle"
        :cardData="featuredArticle"
        :isFeaturedCard="true" />
    </div>

    <div class="magazine-issue-grid__filter">
      <div class="magazine-issue-grid__categories">
        <button 
          v-for="(category, index) in categories" 
          :key="index"
          :class="{'active': category === activeButton}"
          @click="categoryClick(category)">
          {{ category }}
        </button>
      </div>
    </div>

    <div class="magazine-issue-grid__cards">
      <TheMagazineCard
        v-for="(item, index) in articles"
        :key="index"
        :cardData="item"
      />
    </div>

    <div class="magazine-issue-grid__load-more-wrapper">
      <div v-if="infiniteScroll && hasMore" @scroll="handleInfiniteScroll" class="loadMore" ref="loadMoreDiv">Loading...</div>
      <button v-else-if="!infiniteScroll && hasMore" @click="loadMoreBtn">Load More</button>
    </div>

  </div>
</template>

<script lang="javascript">
import { ref, onMounted, onUnmounted, watch, defineAsyncComponent } from 'vue';
import { eventListenerDebouncer, isInViewport } from "v-js-utils";
import axios from 'axios';

import('Assets/scss/components/vue/blocks/magazine-issue-grid.scss');

export default {
  name: "MagazineIssueGrid",
  components: {
    TheMagazineCard: defineAsyncComponent(() => import('../components/cards/the-magazine-card.vue'))
  },
  data() {
    return {
      featuredArticle: {},
      articles: [],
      categories: [],
      currentCategory: null,
      settings: [],
      infiniteScroll: true,
      page: 1,
      isLoading: false,
      currentOffset: 0,
      postsPerPage: 8,
      hasMore: true,
      debouncedHandleResize: null,
      debouncedHandleInfiniteScroll: null,
    };
  },
  beforeMount() {
    this.settings = JSON.parse(this.$attrs.settings);
    this.categories = ['All', ...JSON.parse(this.$attrs.filter)];
    const magazineData = JSON.parse(this.$attrs.magazine);
    const [, ...tempArticles] = magazineData;
    this.featuredArticle = magazineData[0];
    this.articles = tempArticles;

    const urlParams = new URLSearchParams(window.location.search);
    const cat = urlParams.get('cat');
    if (cat) {
      this.currentCategory = cat;
      this.settings.currentOffset = 0;
      this.fetchArticles(cat, true); 
    } else {
      this.currentCategory = "All";
    }
  },
  mounted() {
    this.loadMoreDiv = this.$refs.loadMoreDiv;
    this.currentOffset = Number(this.settings.currentOffset);

    // Add debounced resize listener
    this.debouncedHandleResize = (event) => this.handleResize(event);
    eventListenerDebouncer(window, 'resize', this.debouncedHandleResize, 200);
    
    // Add debounced scroll listener
    this.debouncedHandleInfiniteScroll = (event) => this.handleInfiniteScroll(event);
    eventListenerDebouncer(window, 'scroll', this.debouncedHandleInfiniteScroll, 200);

    // Call once on mount to set initial state
    this.handleResize();
  },
  beforeDestroy() {
    // Remove debounced resize and scroll listeners
    window.removeEventListener('resize', this.debouncedHandleResize);
    window.removeEventListener('scroll', this.debouncedHandleInfiniteScroll);
  },
  computed: {
    activeButton() {
      if (this.currentCategory === null || this.currentCategory === "All") {
        return "All";
      }

      return this.currentCategory;
    }
  },
  methods: {
    categoryClick(cat) {
      this.currentCategory = cat;
      const newURL = new URL(window.location.href);
      newURL.searchParams.set('cat', cat);
      history.pushState({}, '', newURL);

      this.articles = [];
      this.currentOffset = 0;
      this.fetchArticles(cat, true); 
    },
    handleResize(event) {
      // Check the window width
      const windowWidth = window.innerWidth;

      if (windowWidth >= 1024) {
        this.infiniteScroll = this.settings.loadMoreType.desktop;
        this.postsPerPage = this.settings.articlesToLoad.desktop;
      } else if (windowWidth >= 768) {
        this.infiniteScroll = this.settings.loadMoreType.tablet;
        this.postsPerPage = this.settings.articlesToLoad.tablet;
      } else {
        this.infiniteScroll = this.settings.loadMoreType.mobile;
        this.postsPerPage = this.settings.articlesToLoad.mobile;
      }
    },
    handleInfiniteScroll(event) {
      if (this.infiniteScroll && isInViewport(this.loadMoreDiv) && this.hasMore && !this.isLoading) {
        this.fetchArticles(this.currentCategory);
      }
    },
    loadMoreBtn() {
      if (!this.isLoading) {
        this.fetchArticles(this.currentCategory);
      }
    },
    isButtonActive(category, index) {
      // Activate the first button (index 0) if the current category is null or "All"
      if (index === 0 && (this.currentCategory === null || this.currentCategory === "All")) {
        return true;
      }
      // Activate based on current category
      return this.currentCategory === category;
    },
    async fetchArticles(cat = null, shouldReplace = false) {
      this.isLoading = true;

      try {
        const params = {
          initial_loaded_posts: this.currentOffset,
          posts_per_page: this.postsPerPage,
        };

        // Add year to the params if it's specified
        if (cat && cat !== 'All') {
          params.cat = cat;
        }

        const response = await axios.get(`/wp-json/magazine-posts/v1/get_magazine_posts`, {
          params
        });

        const newArticles = response.data.posts;
        
        if (shouldReplace) {
          this.articles = newArticles;
        } else {
          this.articles = [...this.articles, ...newArticles];
        }

        this.currentOffset += newArticles.length;
        this.hasMore = response.data.hasMore;
      } catch (error) {
        console.error('There was a problem:', error);
      } finally {
        this.isLoading = false;
      }
    },
  }
};
</script>
